import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import clsx from 'clsx'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../../../../../assets/svgIcons/LoaderAdm.json'
// import HeadingTitle from '../../../../../../modules/ui/HeadingTitle'
import Table from './Table'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import { useGlobalContext } from '../../../../../../../context/GlobalState'
// import { sileo } from 'sileo'
// import { getErrorMessage } from '../../utils/utils'
import CreditNotePreviewView from '../createNotaCredito/CreditNotePreviewView'
import FirmaNotaCredito from '../createNotaCredito/FirmaNotaCredito'

const DTE_OPTIONS = [
  { label: 'Todos los documentos', value: '' },
  { label: '33 - Factura Electrónica', value: '33' },
  { label: '34 - Factura Exenta Electrónica', value: '34' },
  { label: '39 - Boleta Electrónica', value: '39' },
  { label: '46 - Factura de Compra Electrónica', value: '46' },
  { label: '52 - Guía de Despacho Electrónica', value: '52' },
  { label: '61 - Nota de Crédito Electrónica', value: '61' },
  { label: '80 - Boleta de Honorarios Electrónica', value: '80' },
  { label: '90 - Boleta de Honorarios a Terceros Electrónica', value: '90' },
  { label: '110 - Factura de Exportación Electrónica', value: '110' }
]

export default function ListadoFacturas({ onCloneRequest }) {
  const { currentBusiness, masterEntityData } = useGlobalContext()
  const masterEntityId = masterEntityData?.id
  const facturaApi = useMemo(() => new FacturaAPI(), [])
  const requestIdRef = useRef(0)

  // (front) filtros que NO van al backend
  const [uiFilters, setUiFilters] = useState({
    folio: '',
    rut: '',
    razonSocial: '',
    dteCode: ''
  })

  // (server) filtros que SÍ gatillan API
  const [docType, setDocType] = useState('') // '' | 'received' | 'issued'

  const [isLoading, setIsLoading] = useState(false)
  const [loadingActionByKey, setLoadingActionByKey] = useState({}) // key -> 'pdf' | 'xml'

  const [rawData, setRawData] = useState([])
  const [page, setPage] = useState(1)
  const pageSize = 100
  console.log(page)
  // ---- Lazy-load cache de detalles ----
  // Estado para que la UI se entere (loader, etc.)
  const [detailsByKey, setDetailsByKey] = useState({})
  // Ref con el cache real para NO depender de deps (y evitar recreación de callbacks)
  const detailsRef = useRef({})
  // Para evitar requests duplicadas por doble click
  const inflightRef = useRef({})

  // Step: 1 listado, 2 preview NC, 3 Firma NC
  const [step, setStep] = useState(1)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [ncPayload, setNcPayload] = useState(null)

  const setDetail = useCallback((key, value) => {
    detailsRef.current[key] = value
    setDetailsByKey(prev => ({ ...prev, [key]: value }))
  }, [])

  const getRowKey = useCallback(
    row => `${masterEntityId}:${row.__document_type}:${row.folio}`,
    [masterEntityId]
  )

  const fetchDetailIfNeeded = useCallback(
    async row => {
      const masterId = Number(masterEntityId)
      if (!masterId) return null

      const key = getRowKey(row)

      // 1) cache (ref)
      const cached = detailsRef.current[key]
      if (cached?.status === 'success') return cached

      // 2) request en vuelo (ref)
      if (inflightRef.current[key]) return inflightRef.current[key]

      // 3) marcar loading
      setDetail(key, { status: 'loading', pdf: null, xml: null, error: null })

      // 4) pedir detalle
      const p = (async () => {
        try {
          const resDetalle = await facturaApi.getDocuments({
            master_entity_id: masterId,
            document_type: row.__document_type,
            folio: row.folio
          })

          const d = resDetalle?.data?.results?.[0] || {}

          const value = {
            status: 'success',
            pdf: d.pdf ?? null,
            xml: d.xml ?? null,
            error: null
          }

          setDetail(key, value)
          return value
        } catch (err) {
          const value = {
            status: 'error',
            pdf: null,
            xml: null,
            error: err?.response?.data || err?.message || 'Error'
          }
          setDetail(key, value)
          return value
        } finally {
          delete inflightRef.current[key]
        }
      })()

      inflightRef.current[key] = p
      return p
    },
    [facturaApi, masterEntityId, getRowKey, setDetail]
  )
  const downloadFromUrl = async (url, filename) => {
    const res = await fetch(url)
    if (!res.ok) throw new Error('No se pudo descargar')

    const blob = await res.blob()
    const href = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()

    URL.revokeObjectURL(href)
  }
  const openFile = useCallback(
    async (row, type) => {
      const key = getRowKey(row)

      // si ya tengo cache, no vuelvo a pedir
      const cached = detailsRef.current[key]
      if (cached?.status === 'success') {
        const url = type === 'pdf' ? cached.pdf : cached.xml
        if (!url) return

        if (type === 'pdf') {
          window.open(url, '_blank', 'noopener,noreferrer')
        } else {
          await downloadFromUrl(url, `DTE_${row.folio}.xml`)
        }
        return
      }

      setLoadingActionByKey(prev => ({
        ...prev,
        [key]: { ...prev[key], [type]: true }
      }))

      try {
        const detail = await fetchDetailIfNeeded(row)
        const url = type === 'pdf' ? detail?.pdf : detail?.xml
        if (!url) return

        if (type === 'pdf') {
          window.open(url, '_blank', 'noopener,noreferrer')
        } else {
          await downloadFromUrl(url, `DTE_${row.folio}.xml`)
        }
      } finally {
        setLoadingActionByKey(prev => {
          const currentForKey = prev[key]
          if (!currentForKey || !currentForKey[type]) return prev
          const copy = { ...prev }
          const newActions = { ...currentForKey }
          delete newActions[type]

          if (Object.keys(newActions).length === 0) {
            delete copy[key]
          } else {
            copy[key] = newActions
          }
          return copy
        })
      }
    },
    [fetchDetailIfNeeded, getRowKey]
  )

  const pillInputBase =
    'border border-gray-300 hover:border-gray-400 focus:border-gray-400 focus:outline-none px-4 py-1 pr-6 rounded-full shadow leading-tight text-sm bg-white'

  // 1) master_entity_id now comes from GlobalContext, so we don't need local fetch logic here.
  useEffect(() => {
    if (masterEntityId) {
      setPage(1)
    }
  }, [masterEntityId])

  // 2) Fetch listado (SIN detalle)
  const fetchData = useCallback(
    async (pageToUse = 1) => {
      const masterId = Number(masterEntityId)
      if (!masterId) {
        setRawData([])
        return
      }

      const currentReqId = ++requestIdRef.current
      setIsLoading(true)

      try {
        const typesToFetch = docType ? [docType] : ['received', 'issued']

        const commonParams = {
          master_entity_id: masterId,
          page: pageToUse,
          page_size: pageSize,
          _t: Date.now() // Romper la caché del navegador/proxy
        }

        const listResponses = await Promise.all(
          typesToFetch.map(t => facturaApi.getDocuments({ ...commonParams, document_type: t }))
        )
        if (requestIdRef.current !== currentReqId) return

        const listado = listResponses
          .flatMap((r, idx) => {
            const t = typesToFetch[idx]
            const results = r.data?.results || []
            return results.map(item => ({
              ...item,
              __document_type: t // para saber con qué tipo pedir detalle
            }))
          })
          .sort((a, b) => new Date(b.date_issued || 0) - new Date(a.date_issued || 0))

        // ✅ IMPORTANTE: ya NO hidrata pdf/xml aquí
        setRawData(listado)
      } catch (error) {
        console.error('Error fetching documents', error)
        // sileo.error({
        //   title: '',
        //   description: getErrorMessage(error, 'No se pudieron cargar los documentos')
        // })
        if (requestIdRef.current === currentReqId) setRawData([])
      } finally {
        if (requestIdRef.current === currentReqId) setIsLoading(false)
      }
    },
    [masterEntityId, docType, facturaApi]
  )

  // 3) Refetch automático al cambiar filtros de servidor o master_entity_id
  useEffect(() => {
    if (!masterEntityId) return
    setPage(1)
    fetchData(1)
  }, [masterEntityId, docType, fetchData])

  // 4) Filtrado en front
  const filteredRows = useMemo(() => {
    const folio = uiFilters.folio.trim().toLowerCase()
    const rut = uiFilters.rut.trim().toLowerCase()
    const razon = uiFilters.razonSocial.trim().toLowerCase()
    const dte = uiFilters.dteCode.trim()

    const normalizeRut = s =>
      String(s ?? '')
        .replace(/\./g, '')
        .toLowerCase()

    return (rawData || []).filter(r => {
      if (
        folio &&
        !String(r.folio ?? '')
          .toLowerCase()
          .includes(folio)
      )
        return false

      if (rut) {
        const rutN = normalizeRut(rut)
        const senderRut = normalizeRut(r.sender_tax_id)
        const receiverRut = normalizeRut(r.receiver_tax_id)
        if (!senderRut.includes(rutN) && !receiverRut.includes(rutN)) return false
      }

      if (razon) {
        const sName = String(r.sender_name ?? '').toLowerCase()
        const rName = String(r.receiver_name ?? '').toLowerCase()
        if (!sName.includes(razon) && !rName.includes(razon)) return false
      }

      if (dte && String(r.dte_type_code ?? '') !== dte) return false

      return true
    })
  }, [rawData, uiFilters])

  const handleEmitNC = useCallback(row => {
    setSelectedDoc(row)
    setStep(2)
  }, [])

  const handleNCBack = useCallback(() => {
    if (step === 2) {
      setStep(1)
      setSelectedDoc(null)
    } else if (step === 3) {
      setStep(2)
    }
  }, [step])

  const handleNCConfirm = useCallback(payload => {
    setNcPayload(payload)
    setStep(3)
  }, [])

  const handleEmitSuccess = useCallback(() => {
    fetchData(1)
    setStep(1)
    setSelectedDoc(null)
    setNcPayload(null)
  }, [fetchData])

  const handleCloneDoc = useCallback(
    async row => {
      const masterId = Number(masterEntityId)
      const key = getRowKey(row)

      if (!masterId || !row.id) return

      setLoadingActionByKey(prev => ({
        ...prev,
        [key]: { ...prev[key], clone: true }
      }))

      try {
        const fullDocRes = await facturaApi.getDocument(row.id)
        const fullDoc = fullDocRes?.data || row

        onCloneRequest?.(fullDoc)
      } catch (err) {
        console.error('Error fetching full doc for clone:', err)
      } finally {
        setLoadingActionByKey(prev => {
          const currentForKey = prev[key]
          if (!currentForKey || !currentForKey.clone) return prev
          const copy = { ...prev }
          const newActions = { ...currentForKey }
          delete newActions['clone']

          if (Object.keys(newActions).length === 0) {
            delete copy[key]
          } else {
            copy[key] = newActions
          }
          return copy
        })
      }
    },
    [facturaApi, masterEntityId, getRowKey, onCloneRequest]
  )

  const dynamicDteOptions = useMemo(() => {
    // 1. Obtenemos un Set con todos los códigos que existen en la data actual
    const existingCodes = new Set(rawData.map(r => String(r.dte_type_code || '')))
    // 2. Filtramos el diccionario original
    return DTE_OPTIONS.filter(opt => opt.value === '' || existingCodes.has(opt.value))
  }, [rawData])

  if (step === 2 && selectedDoc) {
    return (
      <div className='min-h-screen w-full p-6 flex justify-center bg-gray-50'>
        <div className='w-full max-w-5xl'>
          <CreditNotePreviewView
            currentBusiness={currentBusiness}
            baseDoc={selectedDoc}
            onBack={handleNCBack}
            onConfirm={handleNCConfirm}
          />
        </div>
      </div>
    )
  }

  if (step === 3 && selectedDoc && ncPayload) {
    return (
      <div className='min-h-screen w-full p-6 flex justify-center bg-gray-50'>
        <div className='w-full max-w-5xl'>
          <FirmaNotaCredito
            document={ncPayload}
            baseDoc={selectedDoc}
            currentBusiness={currentBusiness}
            onBack={handleNCBack}
            onEmitSuccess={handleEmitSuccess}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='bg-white p-4 rounded-3xl border border-slate-200'>
      {/* <HeadingTitle title='Listado de Facturas' iconHeight={32} /> */}

      {/* Barra de Filtros y Botón Actualizar */}
      <div className='mb-6 '>
        <div className='border border-gray-200 rounded-3xl p-4 bg-gray-50 shadow-sm'>
          <div className='flex flex-wrap items-center gap-2'>
            {/* Tipo (server) */}
            <select
              className={clsx(pillInputBase, 'appearance-none cursor-pointer pr-8 min-w-[140px]')}
              value={docType}
              onChange={e => setDocType(e.target.value)}
              title='Tipo de Facturas'
            >
              <option value=''>Todos</option>
              <option value='received'>Recibidos</option>
              <option value='issued'>Emitidos</option>
            </select>

            {/* DTE (front) */}
            <select
              className={clsx(pillInputBase, 'appearance-none cursor-pointer pr-8 min-w-[220px]')}
              value={uiFilters.dteCode}
              onChange={e => setUiFilters(p => ({ ...p, dteCode: e.target.value }))}
              title='Tipo de Documento'
            >
              {dynamicDteOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Folio (front) */}
            <input
              type='text'
              placeholder='Folio'
              value={uiFilters.folio}
              onChange={e => setUiFilters(p => ({ ...p, folio: e.target.value }))}
              className={clsx(pillInputBase, 'min-w-[120px]')}
            />

            {/* RUT (front) */}
            <input
              type='text'
              placeholder='RUT'
              value={uiFilters.rut}
              onChange={e => setUiFilters(p => ({ ...p, rut: e.target.value }))}
              className={clsx(pillInputBase, 'min-w-[180px]')}
            />

            {/* Razón Social (front) */}
            <input
              type='text'
              placeholder='Razón social'
              value={uiFilters.razonSocial}
              onChange={e => setUiFilters(p => ({ ...p, razonSocial: e.target.value }))}
              className={clsx(pillInputBase, 'min-w-[260px]')}
            />

            {/* Botón Actualizar (Ahora dentro del mismo contenedor) */}
            <button
              className='rounded-full bg-accent2 text-white text-sm px-4 py-2 shadow hover:shadow-lg hover:bg-opacity-90'
              onClick={() => fetchData(1)}
              type='button'
              disabled={isLoading}
            >
              Actualizar
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center pt-12 h-64'>
          <Lottie animationData={LoaderAdm} loop style={{ width: 150, height: 150 }} />
        </div>
      ) : (
        <Table
          rows={filteredRows}
          docType={docType}
          className='h-[50vh] rounded bg-white mt-4'
          detailsByKey={detailsByKey}
          loadingActionByKey={loadingActionByKey}
          getRowKey={getRowKey}
          onOpenPdf={row => openFile(row, 'pdf')}
          onOpenXml={row => openFile(row, 'xml')}
          onEmitNC={handleEmitNC}
          onCloneDoc={handleCloneDoc}
        />
      )}
      <style>{sharedBtn}</style>
    </div>
  )
}

const sharedBtn = `
  .btn-detalle {
    padding: 0.45rem 1.4rem;
    border-radius: 5px;
    background: #f26c22;
    color: #ffffff;
    font-size: 0.85rem;
    font-weight: 600;
    border: 1px solid #d35400;
    cursor: pointer;
  }
  .btn-detalle:hover {
    background: #000033;
    border-color: #000033;
  }
`
